// Copyright (C) 2026, Lifefinder Systems International AB

import type { AreaShape, LatLng } from '../../../types';

export function buildCircleShape(center: LatLng, radiusMetres: number): AreaShape {
  return {
    type: 'circle',
    coords: [center],
    radius: radiusMetres,
  };
}

export function buildRectangleShape(corner1: LatLng, corner2: LatLng): AreaShape {
  const [lat1, lng1] = corner1;
  const [lat2, lng2] = corner2;
  return {
    type: 'rectangle',
    coords: [
      [lat1, lng1],
      [lat1, lng2],
      [lat2, lng2],
      [lat2, lng1],
    ],
  };
}

export function buildPolygonShape(vertices: LatLng[]): AreaShape {
  return {
    type: 'polygon',
    coords: vertices,
  };
}

/**
 * Returns an axis-aligned bounding box [sw, ne] for the given shape.
 * For circles the box is derived from the radius; for rectangle/polygon
 * it is the min/max of all coordinate pairs.
 */
export function getAreaBounds(shape: AreaShape): [LatLng, LatLng] {
  if (shape.type === 'circle') {
    const [lat, lng] = shape.coords[0];
    const r = shape.radius ?? 0;
    const dLat = r / 111320;
    const dLng = r / (111320 * Math.cos((lat * Math.PI) / 180));
    return [
      [lat - dLat, lng - dLng],
      [lat + dLat, lng + dLng],
    ];
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const [lat, lng] of shape.coords) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }

  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}
